// src/config/api.config.ts

/**
 * API Configuration
 * Centralized configuration for all API-related settings
 */

export interface ApiConfig {
  baseURL: string;
  timeout: number;
  clientVersion: string;
  isDevelopment: boolean;
  retry: {
    attempts: number;
    delay: number;
    maxDelay: number;
    backoffFactor: number;
    retryableStatuses: number[];
  };
  headers: Record<string, string>;
  endpoints: {
    auth: Record<string, string>;
    tenant: Record<string, string>;
    user: Record<string, string>;
    invite: Record<string, string>;
    companyCode: Record<string, string>;
  };
}

const API_VERSION = 'v1';

export const apiConfig: ApiConfig = {
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
  timeout: import.meta.env.VITE_API_TIMEOUT ? parseInt(import.meta.env.VITE_API_TIMEOUT) : 30000,
  clientVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',
  isDevelopment: import.meta.env.MODE === 'development',

  retry: {
    attempts: 3,
    delay: 1000,
    maxDelay: 5000,
    backoffFactor: 2,
    retryableStatuses: [408, 429, 500, 502, 503, 504],
  },
  
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Client-Version': import.meta.env.VITE_APP_VERSION || '1.0.0',
    'X-Client-Platform': 'web',
  },
  
  endpoints: {
    auth: {
      login: `/api/${API_VERSION}/auth/login`,
      logout: `/api/${API_VERSION}/auth/logout`,
      register: `/api/${API_VERSION}/auth/register`,
      refresh: `/api/${API_VERSION}/auth/refresh`,
      me: `/api/${API_VERSION}/auth/me`,
      forgotPassword: `/api/${API_VERSION}/auth/forgot-password`,
      resetPassword: `/api/${API_VERSION}/auth/reset-password`,
      verifyEmail: `/api/${API_VERSION}/auth/verify-email`,
      resendVerification: `/api/${API_VERSION}/auth/resend-verification`,
      changePassword: `/api/${API_VERSION}/auth/change-password`,
      
      // MFA endpoints
      mfaSetup: `/api/${API_VERSION}/auth/mfa/setup`,
      mfaVerify: `/api/${API_VERSION}/auth/mfa/verify`,
      mfaDisable: `/api/${API_VERSION}/auth/mfa/disable`,
      mfaBackupCodes: `/api/${API_VERSION}/auth/mfa/backup-codes`,
      
      // Session management
      sessions: `/api/${API_VERSION}/auth/sessions`,
      revokeSession: `/api/${API_VERSION}/auth/sessions/:sessionId/revoke`,
    },
    
    tenant: {
      validate: `/api/${API_VERSION}/tenants/workspace/:workspace/validate`,
      validatePost: `/api/${API_VERSION}/auth/workspace/validate`,
      create: `/api/${API_VERSION}/tenants/create`,
      get: `/api/${API_VERSION}/tenants/:tenantId`,
      update: `/api/${API_VERSION}/tenants/:tenantId`,
      delete: `/api/${API_VERSION}/tenants/:tenantId`,
      list: `/api/${API_VERSION}/tenants`,

      // Tenant-specific
      members: `/api/${API_VERSION}/tenants/:tenantId/members`,
      invite: `/api/${API_VERSION}/tenants/:tenantId/invite`,
      settings: `/api/${API_VERSION}/tenants/:tenantId/settings`,
      billing: `/api/${API_VERSION}/tenants/:tenantId/billing`,
      usage: `/api/${API_VERSION}/tenants/:tenantId/usage`,

      // Subdomain
      checkAvailability: `/api/${API_VERSION}/tenants/check-availability`,
      bySubdomain: `/api/${API_VERSION}/tenants/subdomain/:subdomain`,
    },
    
    user: {
      profile: `/api/${API_VERSION}/users/profile`,
      updateProfile: `/api/${API_VERSION}/users/profile`,
      uploadAvatar: `/api/${API_VERSION}/users/avatar`,
      deleteAvatar: `/api/${API_VERSION}/users/avatar`,
      preferences: `/api/${API_VERSION}/users/preferences`,
      notifications: `/api/${API_VERSION}/users/notifications`,
      
      // User management (admin)
      list: `/api/${API_VERSION}/users`,
      get: `/api/${API_VERSION}/users/:userId`,
      create: `/api/${API_VERSION}/users`,
      update: `/api/${API_VERSION}/users/:userId`,
      delete: `/api/${API_VERSION}/users/:userId`,
      suspend: `/api/${API_VERSION}/users/:userId/suspend`,
      activate: `/api/${API_VERSION}/users/:userId/activate`,
    },
    
    invite: {
      validate: `/api/${API_VERSION}/invites/:token/validate`,
      accept: `/api/${API_VERSION}/invites/:token/accept`,
      decline: `/api/${API_VERSION}/invites/:token/decline`,
      resend: `/api/${API_VERSION}/invites/:inviteId/resend`,
      list: `/api/${API_VERSION}/invites`,
      create: `/api/${API_VERSION}/invites`,
      revoke: `/api/${API_VERSION}/invites/:inviteId/revoke`,
      bulkInvite: `/api/${API_VERSION}/invites/bulk`,
    },
    
    companyCode: {
      validate: `/api/${API_VERSION}/company-codes/:code/validate`,
      use: `/api/${API_VERSION}/company-codes/:code/use`,
      generate: `/api/${API_VERSION}/company-codes/generate`,
      list: `/api/${API_VERSION}/company-codes`,
      revoke: `/api/${API_VERSION}/company-codes/:code/revoke`,
      stats: `/api/${API_VERSION}/company-codes/:code/stats`,
    },
  },
};

// Helper function to build URLs with parameters
export function buildUrl(endpoint: string, params?: Record<string, string>): string {
  let url = endpoint;
  
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url = url.replace(`:${key}`, encodeURIComponent(value));
    });
  }
  
  return url;
}

// Rate limiting configuration
export const rateLimits = {
  login: {
    maxAttempts: 5,
    windowMs: 15 * 60 * 1000, // 15 minutes
  },
  register: {
    maxAttempts: 3,
    windowMs: 60 * 60 * 1000, // 1 hour
  },
  passwordReset: {
    maxAttempts: 3,
    windowMs: 60 * 60 * 1000, // 1 hour
  },
  api: {
    maxRequests: 100,
    windowMs: 60 * 1000, // 1 minute
  },
};

// Webhook configuration
export const webhookConfig = {
  endpoints: {
    userCreated: `/api/${API_VERSION}/webhooks/user-created`,
    userDeleted: `/api/${API_VERSION}/webhooks/user-deleted`,
    tenantCreated: `/api/${API_VERSION}/webhooks/tenant-created`,
    subscriptionChanged: `/api/${API_VERSION}/webhooks/subscription-changed`,
  },
  timeout: 5000,
  retryAttempts: 3,
};

// File upload configuration
export const uploadConfig = {
  maxFileSize: 5 * 1024 * 1024, // 5MB
  allowedMimeTypes: {
    avatar: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    documents: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    images: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
  },
  endpoints: {
    avatar: `/api/${API_VERSION}/upload/avatar`,
    document: `/api/${API_VERSION}/upload/document`,
    image: `/api/${API_VERSION}/upload/image`,
  },
};

export default apiConfig;